import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { MidiClip } from '@/domain/types';
import { signatureMapOf } from '@/domain/selectors';
import { snapStepTicks, snapTick, SNAP_LABELS, SNAP_VALUES } from '@/timing/snap';
import { signatureSegments, ticksPerBar, ticksPerBeat } from '@/timing/tempoMap';
import { isBlackKey, midiNoteName } from '@/engine/instruments/descriptors';
import { midiClipPitchRange } from '@/engine/midiPlan';
import { getServices } from '@/state/services';
import { useDaw } from '@/state/store';
import { createId } from '@/util/id';

/**
 * The piano roll.
 *
 * It shares the arrangement's coordinate discipline — positions are ticks,
 * snapping is computed on the grid rather than in pixels — but keeps its own
 * horizontal zoom, because the useful zoom for editing sixteenth notes is not
 * the useful zoom for arranging four-minute takes.
 *
 * Notes are drawn on a canvas for the same reason clips are: a busy part is
 * thousands of rectangles, and a DOM node each would make every drag a layout
 * pass.
 */

const MIN_ROW = 8;
const MAX_ROW = 28;
const KEYBOARD_WIDTH = 44;
const EDGE_GRAB_PX = 6;

type Gesture =
  | { kind: 'none' }
  | { kind: 'move'; noteIds: string[]; startTick: number; startPitch: number; appliedTicks: number; appliedPitch: number }
  | { kind: 'resize'; noteIds: string[]; anchorTick: number }
  | { kind: 'marquee'; x0: number; y0: number; x1: number; y1: number };

export function PianoRoll(): JSX.Element {
  const project = useDaw((s) => s.project);
  const selection = useDaw((s) => s.selection);
  const dispatch = useDaw((s) => s.dispatch);
  const setSelection = useDaw((s) => s.setSelection);

  const clip = selection.clipIds.length === 1 ? project.clips[selection.clipIds[0]] : null;
  const midiClip: MidiClip | null = clip && clip.kind === 'midi' ? clip : null;

  const wrapRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const gestureRef = useRef<Gesture>({ kind: 'none' });

  const [size, setSize] = useState({ width: 0, height: 0 });
  const [rowHeight, setRowHeight] = useState(14);
  const [topPitch, setTopPitch] = useState(84);
  const [pixelsPerBeat, setPixelsPerBeat] = useState(96);
  const [scrollTicks, setScrollTicks] = useState(0);
  const [marquee, setMarquee] = useState<{ x0: number; y0: number; x1: number; y1: number } | null>(null);
  const [framedClipId, setFramedClipId] = useState<string | null>(null);

  const ppq = project.transport.ppq;
  const sigMap = useMemo(() => signatureMapOf(project), [project]);
  const selectedNotes = useMemo(() => new Set(selection.noteIds), [selection.noteIds]);

  useEffect(() => {
    const element = wrapRef.current;
    if (!element) return;
    const observer = new ResizeObserver((entries) => {
      const rect = entries[0].contentRect;
      setSize({ width: Math.floor(rect.width), height: Math.floor(rect.height) });
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  // Frame a newly opened clip so its notes are on screen without any scrolling.
  useEffect(() => {
    if (!midiClip || midiClip.id === framedClipId || size.height === 0) return;
    const { low, high } = midiClipPitchRange(midiClip);
    const rows = Math.max(1, Math.floor(size.height / rowHeight));
    const centre = Math.round((low + high) / 2);
    setTopPitch(Math.min(127, Math.max(rows - 1, centre + Math.floor(rows / 2))));
    setScrollTicks(0);
    setFramedClipId(midiClip.id);
  }, [midiClip, framedClipId, size.height, rowHeight]);

  const tickToX = useCallback(
    (tick: number) => ((tick - scrollTicks) / ppq) * pixelsPerBeat,
    [scrollTicks, ppq, pixelsPerBeat],
  );
  const xToTick = useCallback(
    (x: number) => scrollTicks + (x / pixelsPerBeat) * ppq,
    [scrollTicks, ppq, pixelsPerBeat],
  );
  const pitchToY = useCallback((pitch: number) => (topPitch - pitch) * rowHeight, [topPitch, rowHeight]);
  const yToPitch = useCallback((y: number) => topPitch - Math.floor(y / rowHeight), [topPitch, rowHeight]);

  const snap = useCallback(
    (tick: number) => snapTick(sigMap, project.transport.snap, tick),
    [sigMap, project.transport.snap],
  );

  // -- drawing -------------------------------------------------------------

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || size.width === 0 || !midiClip) return;
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    canvas.width = Math.floor(size.width * dpr);
    canvas.height = Math.floor(size.height * dpr);
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.save();
    ctx.scale(dpr, dpr);
    ctx.clearRect(0, 0, size.width, size.height);

    // Rows: black keys darker, so the octave is readable at a glance.
    const rows = Math.ceil(size.height / rowHeight) + 1;
    for (let i = 0; i < rows; i++) {
      const pitch = topPitch - i;
      ctx.fillStyle = isBlackKey(pitch) ? '#0a0c0f' : '#0e1116';
      ctx.fillRect(0, i * rowHeight, size.width, rowHeight);
      if (pitch % 12 === 0) {
        ctx.fillStyle = '#232932';
        ctx.fillRect(0, i * rowHeight, size.width, 1);
      }
    }

    // Grid. Ticks here are clip-relative, but the meter is a property of the
    // project timeline, so every line is resolved through the clip's position:
    // a clip that starts mid-bar, or spans a meter change, still gets the bar
    // lines that the arrangement shows underneath it.
    const lastTick = xToTick(size.width);
    for (const segment of signatureSegments(
      sigMap,
      midiClip.startTicks + scrollTicks,
      midiClip.startTicks + lastTick,
    )) {
      const barTicks = ticksPerBar(segment.sig, ppq);
      const beatTicks = ticksPerBeat(segment.sig, ppq);
      const step = snapStepTicks(sigMap, project.transport.snap, segment.startTick) || beatTicks;
      const from = Math.max(midiClip.startTicks + scrollTicks, segment.startTick);
      const to = Math.min(midiClip.startTicks + lastTick, segment.endTick);
      const first = segment.startTick + Math.floor((from - segment.startTick) / step) * step;
      for (let absolute = first; absolute <= to; absolute += step) {
        if (absolute < segment.startTick) continue;
        const rel = absolute - segment.startTick;
        const isBar = rel % barTicks === 0;
        const isBeat = rel % beatTicks === 0;
        ctx.fillStyle = isBar ? '#2b323c' : isBeat ? '#1c222a' : '#141920';
        ctx.fillRect(Math.round(tickToX(absolute - midiClip.startTicks)), 0, 1, size.height);
      }
    }

    // Everything past the clip's own length is dimmed: notes there never sound.
    const clipEndX = tickToX(midiClip.durationTicks);
    if (clipEndX < size.width) {
      ctx.fillStyle = 'rgba(4, 5, 7, 0.55)';
      ctx.fillRect(clipEndX, 0, size.width - clipEndX, size.height);
    }

    const track = project.tracks[midiClip.trackId];
    const colour = track?.color ?? '#5b8def';

    for (const note of midiClip.notes) {
      const x = tickToX(note.startTicks);
      const width = Math.max(2, (note.durationTicks / ppq) * pixelsPerBeat);
      const y = pitchToY(note.pitch);
      if (x + width < 0 || x > size.width || y + rowHeight < 0 || y > size.height) continue;

      const selected = selectedNotes.has(note.id);
      // Velocity is shown as opacity, so dynamics are visible without a
      // separate lane taking up space.
      const alpha = 0.35 + (note.velocity / 127) * 0.6;
      ctx.fillStyle = withAlpha(colour, alpha);
      ctx.fillRect(x, y + 1, width, rowHeight - 2);
      ctx.strokeStyle = selected ? '#cfe0ff' : withAlpha(colour, 0.9);
      ctx.lineWidth = selected ? 1.6 : 1;
      ctx.strokeRect(x + 0.5, y + 1.5, width - 1, rowHeight - 3);
    }

    if (marquee) {
      const mx = Math.min(marquee.x0, marquee.x1);
      const my = Math.min(marquee.y0, marquee.y1);
      ctx.fillStyle = 'rgba(79, 143, 247, 0.16)';
      ctx.fillRect(mx, my, Math.abs(marquee.x1 - marquee.x0), Math.abs(marquee.y1 - marquee.y0));
      ctx.strokeStyle = 'rgba(127, 176, 255, 0.8)';
      ctx.strokeRect(mx + 0.5, my + 0.5, Math.abs(marquee.x1 - marquee.x0), Math.abs(marquee.y1 - marquee.y0));
    }

    ctx.restore();
  }, [
    midiClip,
    project.tracks,
    project.transport.snap,
    size,
    rowHeight,
    topPitch,
    pixelsPerBeat,
    scrollTicks,
    selectedNotes,
    marquee,
    sigMap,
    ppq,
    tickToX,
    xToTick,
    pitchToY,
  ]);

  // -- gestures ------------------------------------------------------------

  const noteAt = useCallback(
    (x: number, y: number): { id: string; atEdge: boolean } | null => {
      if (!midiClip) return null;
      const pitch = yToPitch(y);
      for (let i = midiClip.notes.length - 1; i >= 0; i--) {
        const note = midiClip.notes[i];
        if (note.pitch !== pitch) continue;
        const nx = tickToX(note.startTicks);
        const nw = Math.max(2, (note.durationTicks / ppq) * pixelsPerBeat);
        if (x < nx || x > nx + nw) continue;
        return { id: note.id, atEdge: nx + nw - x <= EDGE_GRAB_PX };
      }
      return null;
    },
    [midiClip, yToPitch, tickToX, ppq, pixelsPerBeat],
  );

  const localPoint = (event: { clientX: number; clientY: number }, element: HTMLElement): { x: number; y: number } => {
    const rect = element.getBoundingClientRect();
    return { x: event.clientX - rect.left, y: event.clientY - rect.top };
  };

  const onPointerDown = (event: React.PointerEvent<HTMLCanvasElement>): void => {
    if (!midiClip || event.button !== 0) return;
    const canvas = event.currentTarget;
    canvas.setPointerCapture(event.pointerId);
    const { x, y } = localPoint(event, canvas);
    const hit = noteAt(x, y);
    const additive = event.shiftKey || event.metaKey || event.ctrlKey;

    if (hit && event.altKey) {
      dispatch({ type: 'midi.deleteNotes', params: { clipId: midiClip.id, noteIds: [hit.id] } });
      return;
    }

    if (hit) {
      const noteIds = selectedNotes.has(hit.id)
        ? selection.noteIds
        : additive
          ? [...selection.noteIds, hit.id]
          : [hit.id];
      setSelection({ noteIds });
      const note = midiClip.notes.find((n) => n.id === hit.id)!;
      gestureRef.current = hit.atEdge
        ? { kind: 'resize', noteIds, anchorTick: note.startTicks }
        : { kind: 'move', noteIds, startTick: xToTick(x), startPitch: yToPitch(y), appliedTicks: 0, appliedPitch: 0 };
      return;
    }

    if (event.altKey) {
      setSelection({ noteIds: [] });
      gestureRef.current = { kind: 'marquee', x0: x, y0: y, x1: x, y1: y };
      setMarquee({ x0: x, y0: y, x1: x, y1: y });
      return;
    }

    // Drawing a note: it lands on the grid, one grid step long, and is grabbed
    // immediately so the same gesture can drag it into place.
    const tick = Math.max(0, Math.round(snap(xToTick(x))));
    const step = snapStepTicks(sigMap, project.transport.snap, midiClip.startTicks + tick) || ppq;
    const noteId = createId('not');
    dispatch({
      type: 'midi.addNote',
      params: {
        clipId: midiClip.id,
        noteId,
        pitch: yToPitch(y),
        velocity: 100,
        startTicks: tick,
        durationTicks: Math.max(1, Math.round(step)),
      },
    });
    setSelection({ noteIds: [noteId] });
    gestureRef.current = {
      kind: 'move',
      noteIds: [noteId],
      startTick: xToTick(x),
      startPitch: yToPitch(y),
      appliedTicks: 0,
      appliedPitch: 0,
    };
  };

  const onPointerMove = (event: React.PointerEvent<HTMLCanvasElement>): void => {
    if (!midiClip) return;
    const canvas = event.currentTarget;
    const { x, y } = localPoint(event, canvas);
    const gesture = gestureRef.current;

    if (gesture.kind === 'none') {
      const hit = noteAt(x, y);
      canvas.style.cursor = hit ? (hit.atEdge ? 'ew-resize' : 'grab') : 'crosshair';
      return;
    }

    if (gesture.kind === 'marquee') {
      gestureRef.current = { ...gesture, x1: x, y1: y };
      setMarquee({ x0: gesture.x0, y0: gesture.y0, x1: x, y1: y });
      return;
    }

    if (gesture.kind === 'resize') {
      const end = Math.max(gesture.anchorTick + 1, Math.round(snap(xToTick(x))));
      dispatch({
        type: 'midi.resizeNotes',
        params: { clipId: midiClip.id, noteIds: gesture.noteIds, durationTicks: end - gesture.anchorTick },
      });
      return;
    }

    // Snap the moved note's own position rather than the raw pointer delta.
    const anchor = midiClip.notes.find((n) => n.id === gesture.noteIds[0]);
    if (!anchor) return;
    const rawDelta = xToTick(x) - gesture.startTick;
    const wantedTicks = snap(anchor.startTicks - gesture.appliedTicks + rawDelta) - (anchor.startTicks - gesture.appliedTicks);
    const wantedPitch = yToPitch(y) - gesture.startPitch;
    const deltaTicks = Math.round(wantedTicks - gesture.appliedTicks);
    const deltaPitch = wantedPitch - gesture.appliedPitch;

    if (deltaTicks !== 0 || deltaPitch !== 0) {
      dispatch({
        type: 'midi.moveNotes',
        params: { clipId: midiClip.id, noteIds: gesture.noteIds, deltaTicks, deltaPitch },
      });
      gestureRef.current = {
        ...gesture,
        appliedTicks: gesture.appliedTicks + deltaTicks,
        appliedPitch: gesture.appliedPitch + deltaPitch,
      };
    }
  };

  const onPointerUp = (event: React.PointerEvent<HTMLCanvasElement>): void => {
    const gesture = gestureRef.current;
    gestureRef.current = { kind: 'none' };
    event.currentTarget.releasePointerCapture(event.pointerId);
    getServices().commands.breakCoalescing();

    if (gesture.kind === 'marquee' && midiClip) {
      setMarquee(null);
      const x0 = Math.min(gesture.x0, gesture.x1);
      const x1 = Math.max(gesture.x0, gesture.x1);
      const y0 = Math.min(gesture.y0, gesture.y1);
      const y1 = Math.max(gesture.y0, gesture.y1);
      const hits = midiClip.notes
        .filter((note) => {
          const nx = tickToX(note.startTicks);
          const nw = Math.max(2, (note.durationTicks / ppq) * pixelsPerBeat);
          const ny = pitchToY(note.pitch);
          return nx < x1 && nx + nw > x0 && ny < y1 && ny + rowHeight > y0;
        })
        .map((note) => note.id);
      setSelection({ noteIds: hits });
    }
  };

  useEffect(() => {
    const element = wrapRef.current;
    if (!element) return;
    const onWheel = (event: WheelEvent): void => {
      if (event.ctrlKey || event.metaKey) {
        event.preventDefault();
        setPixelsPerBeat((v) => Math.min(600, Math.max(12, v * Math.pow(1.0025, -event.deltaY))));
        return;
      }
      if (event.shiftKey) {
        event.preventDefault();
        setScrollTicks((v) => Math.max(0, v + (event.deltaY / pixelsPerBeat) * ppq));
        return;
      }
      event.preventDefault();
      setTopPitch((v) => Math.min(127, Math.max(12, v - Math.sign(event.deltaY) * 2)));
    };
    element.addEventListener('wheel', onWheel, { passive: false });
    return () => element.removeEventListener('wheel', onWheel);
  }, [pixelsPerBeat, ppq]);

  if (!midiClip) {
    return (
      <div className="empty-note">
        Select a MIDI clip to edit it. Double-click an instrument track in the arrangement to create one.
      </div>
    );
  }

  const rows = Math.ceil(size.height / rowHeight) + 1;

  return (
    <div className="pianoroll">
      <div className="pianoroll__toolbar">
        <span className="field__label">Editing</span>
        <span style={{ color: 'var(--text-0)' }}>{midiClip.name}</span>
        <span className="field__label">{midiClip.notes.length} notes</span>

        <div className="topbar__spacer" />

        <label className="field" style={{ gap: 4 }}>
          <span className="field__label">Quantize</span>
          <select
            className="numeric"
            style={{ width: 60, cursor: 'pointer' }}
            value=""
            aria-label="Quantize notes"
            onChange={(e) => {
              if (!e.target.value) return;
              dispatch({
                type: 'midi.quantize',
                params: {
                  clipId: midiClip.id,
                  noteIds: selection.noteIds.length > 0 ? selection.noteIds : null,
                  snap: e.target.value,
                  strength: 1,
                },
              });
              e.target.value = '';
            }}
          >
            <option value="">Apply…</option>
            {SNAP_VALUES.filter((v) => v !== 'off').map((value) => (
              <option key={value} value={value}>
                {SNAP_LABELS[value]}
              </option>
            ))}
          </select>
        </label>

        <button
          className={`btn ${midiClip.loop ? 'btn--active' : ''}`}
          onClick={() =>
            dispatch({ type: 'midi.setClipLoop', params: { clipId: midiClip.id, loop: !midiClip.loop } })
          }
          title="Repeat the clip's notes for its whole length"
        >
          Loop notes
        </button>

        <div className="group" role="group" aria-label="Row height">
          <button className="btn" onClick={() => setRowHeight((v) => Math.max(MIN_ROW, v - 2))} aria-label="Shorter rows">
            −
          </button>
          <button className="btn" onClick={() => setRowHeight((v) => Math.min(MAX_ROW, v + 2))} aria-label="Taller rows">
            +
          </button>
        </div>
      </div>

      <div className="pianoroll__body">
        <div className="pianoroll__keys" style={{ width: KEYBOARD_WIDTH }} aria-hidden="true">
          {Array.from({ length: rows }, (_, i) => {
            const pitch = topPitch - i;
            return (
              <div
                key={pitch}
                className={`pianoroll__key ${isBlackKey(pitch) ? 'pianoroll__key--black' : ''}`}
                style={{ height: rowHeight }}
              >
                {pitch % 12 === 0 ? midiNoteName(pitch) : ''}
              </div>
            );
          })}
        </div>
        <div className="pianoroll__grid" ref={wrapRef}>
          <canvas
            ref={canvasRef}
            style={{ width: '100%', height: '100%' }}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerUp}
            aria-label="Piano roll. Click to add a note, drag to move it, drag its right edge to change its length."
          />
        </div>
      </div>
    </div>
  );
}

function withAlpha(hex: string, alpha: number): string {
  const value = hex.replace('#', '');
  const full = value.length === 3 ? value.split('').map((c) => c + c).join('') : value;
  return `rgba(${parseInt(full.slice(0, 2), 16)}, ${parseInt(full.slice(2, 4), 16)}, ${parseInt(full.slice(4, 6), 16)}, ${alpha})`;
}
